/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

require('./bootstrap');

import Vue from 'vue';
import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';
import Toasted from 'vue-toasted';

// Make BootstrapVue plugins available throughout the project
import {PaginationPlugin, TablePlugin, CollapsePlugin} from 'bootstrap-vue';
import {InputGroupPlugin, FormInputPlugin, ButtonPlugin} from 'bootstrap-vue';
import {ModalPlugin, FormGroupPlugin, DropdownPlugin} from 'bootstrap-vue';
import {
  TooltipPlugin,
  FormRadioPlugin,
  ListGroupPlugin,
  BadgePlugin,
} from 'bootstrap-vue';

import Dinero from 'dinero.js';
import Swal from 'sweetalert2/dist/sweetalert2.js';

/**
 * Set up php language files for Javascript, grab locale from meta element.
 */
import Lang from 'lang.js';
import languages from './languages';

Vue.use(ModalPlugin);
Vue.use(TablePlugin);
Vue.use(PaginationPlugin, {
  BPagination: {
    firstText: 'first_page',
    prevText: 'navigate_before',
    nextText: 'navigate_next',
    lastText: 'last_page',
    firstClass: 'material-icons',
    prevClass: 'material-icons',
    nextClass: 'material-icons',
    lastClass: 'material-icons',
  },
});
Vue.use(CollapsePlugin);
Vue.use(ModalPlugin);
Vue.use(InputGroupPlugin);
Vue.use(FormInputPlugin);
Vue.use(ButtonPlugin);
Vue.use(TooltipPlugin);
Vue.use(DropdownPlugin);
Vue.use(FormGroupPlugin);
Vue.use(FormRadioPlugin);
Vue.use(ListGroupPlugin);
Vue.use(BadgePlugin);

// Get all php lang files as a lang.js compatible object.
const messages = languages.get();

window.Lang = new Lang({});
window.Lang.setMessages(messages);

window.Swal = Swal;

Vue.prototype.Lang = window.Lang;
Vue.filter('lang', function(key, replacements) {
  return window.Lang.get(key, replacements);
});

Vue.filter('formatCurrency', (amount, currency, locale) => {
  const loc = typeof locale === 'undefined' ? 'sv-SE' : locale;

  // Needed when currency isn't given, to prevent excessive decimals.
  let style = { maximumFractionDigits: 2 };

  // Fixes so that non-decimal values don't needlessly show decimals (for unspecified currency only)
  if (amount % 1 === 0) {
    style.maximumFractionDigits = 0;
  }

  if (currency) {
    style = {
      style: 'currency',
      currency: currency.toUpperCase(),
    };
  }

  return new Intl.NumberFormat(loc, style).format(amount);
});

Vue.filter('formatMoney', (money, locale) => {
  const loc = typeof locale === 'undefined' ? 'sv-SE' : locale;

  return Dinero({amount: parseInt(money.amount), currency: money.currency}).
      setLocale(loc).
      toFormat();
});

// Avoid injection of {{ in the app
// Vue would otherwise interpolate {{ }} anywhere
const DELIMITER_PATCH = {
  replace() {
    return '^(?!.).';
  },
};
Vue.mixin({
  delimiters: [DELIMITER_PATCH, DELIMITER_PATCH],
});

window.Vue = Vue;

Vue.use(Toasted, {
  duration: 5000,
  keepOnHover: true,
});
Vue.toasted.register('warning',
    (payload) => {
      if (!payload.message) {
        return 'Warning...';
      }

      return payload.message;
    }, {
      type: 'warning',
    },
);
Vue.config.ignoredElements = ['ion-icon'];

// Register available components
function registerComponents(components, namespaces = []) {
  components.keys().forEach(filePath => {
    const componentConfig = components(filePath);

    let fileParts = filePath.split('/');
    let componentName = upperFirst(
        camelCase(fileParts.pop().replace(/\.\w+$/, '')));
    const namespace = fileParts.pop();

    if (namespaces.includes(namespace)) {
      componentName = upperFirst(camelCase(namespace)) + componentName;
    }
    Vue.component(componentName, componentConfig.default || componentConfig);
  });
}

const namespaces = ['invoice', 'service'];
registerComponents(require.context('.', true, /\.vue$/i), namespaces);

/**
 * Vue filter to round the decimal to the given place.
 * http://jsfiddle.net/bryan_k/3ova17y9/
 *
 * @param {String} value    The value string.
 * @param {Number} decimals The number of decimal places.
 */
Vue.filter('round', (value, decimals) => {
  if (!value) {
    value = 0;
  }
  if (!decimals) {
    decimals = 0;
  }
  value = Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);

  return value;
});

/**
 * Next, we will create a fresh Vue application instance and attach it to
 * the page. Then, you may begin adding components to this application
 * or customize the JavaScript scaffolding to fit your unique needs.
 */
Vue.prototype.$eventHub = new Vue(); // Global event bus

const app = new Vue({
  el: '#app',
});
